// tslint:disable-next-line:nx-enforce-module-boundaries
import { isCommercialList } from '@epicuro-next/features/engagement/list-utils';
import { selectListEntities } from '@epicuro-next/state/list-entities';
import { selectLookBookEntitiesState } from '@epicuro-next/state/lookbook-entities';
import { selectUser } from '@epicuro-next/state/user-state';
import { createSelector } from '@ngrx/store';

import { CORE_SHOP_THE_LOOK_FEATURE_KEY } from '../constants';
import { selectShopTheLookState } from '../main-selector';

export const selectCoreShopTheLook = createSelector(
  selectShopTheLookState,
  (state) => state[CORE_SHOP_THE_LOOK_FEATURE_KEY],
);

export const selectLookbookIdInRoute = createSelector(
  selectCoreShopTheLook,
  (state) => state.lookbookIdInRouter,
);

// lookbook
export const selectLookBookEntityInRoute = createSelector(
  selectLookbookIdInRoute,
  selectLookBookEntitiesState,
  (id, { entities }) => (id ? entities[id] : undefined),
);

export const selectLookBookInRoute = createSelector(
  selectLookBookEntityInRoute,
  (entity) => entity?.lookBook,
);

export const selectLookBookInRouteLoading = createSelector(
  selectLookBookEntityInRoute,
  (entity) => entity?.loading,
);

export const selectLookBookInRouteError = createSelector(
  selectLookBookEntityInRoute,
  (entity) => entity?.error,
);

export const selectLookBookListsInRoute = createSelector(
  selectLookBookEntityInRoute,
  (entity) => entity?.lookBook?.items.map((item) => item.productList),
);

export const selectLookBookListsIdsInRoute = createSelector(
  selectLookBookListsInRoute,
  (lists) => lists?.map(({ id }) => id),
);

// list
export const selectLookbookListIdInRoute = createSelector(
  selectCoreShopTheLook,
  selectLookBookEntityInRoute,
  (state, lookBookEntity) =>
    state.lookbookListIdInRouter ??
    lookBookEntity?.lookBook?.items?.[0].productListId,
);

export const selectLookBookListEntityInRoute = createSelector(
  selectListEntities,
  selectLookbookListIdInRoute,
  (entities, id) => (id ? entities[id] : undefined),
);

export const selectLookBookListIsOwned = createSelector(
  selectLookBookListEntityInRoute,
  selectUser,
  (listEntity, user) => {
    const listOwner = listEntity?.list?.creation.user;
    if (!listOwner || !user) return undefined;

    return listOwner.externalId === user.externalId;
  },
);

export const selectLookBookListInRoute = createSelector(
  selectLookBookListEntityInRoute,
  (entity) => entity?.list,
);

export const selectLookBookCommercialListInRoute = createSelector(
  selectLookBookListEntityInRoute,
  (entity) => (entity?.list ? isCommercialList(entity?.list) : false),
);

export const selectLookBookListInRouteLoading = createSelector(
  selectLookBookListEntityInRoute,
  (entity) => entity?.loading,
);

export const selectLookBookListInRouteError = createSelector(
  selectLookBookListEntityInRoute,
  (entity) => entity?.error,
);

export const selectLookBookListsInRouteProductsCount = createSelector(
  selectLookBookListsInRoute,
  (lists) =>
    lists?.map(({ id, availableProductsCount, unavailableProductsCount }) => ({
      id,
      availableProductsCount,
      unavailableProductsCount,
      total: (availableProductsCount ?? 0) + (unavailableProductsCount ?? 0),
    })),
);

export const selectLookBookListsIdsInRouteWithNoProducts = createSelector(
  selectLookBookListsInRoute,
  (lists) =>
    lists
      ?.filter(
        ({ availableProductsCount, unavailableProductsCount }) =>
          !((availableProductsCount ?? 0) + (unavailableProductsCount ?? 0)),
      )
      .map(({ id }) => id),
);

export const selectListEditClickedFromLookbook = createSelector(
  selectCoreShopTheLook,
  (state) => state.listEditClickedFrom,
);

export const selectIsLookbookPromoted = createSelector(
  selectLookBookInRoute,
  (lookbook) => !!lookbook?.newsfeedId,
);

export const selectIsOwnerOfOpenedLookbook = createSelector(
  selectUser,
  selectLookBookInRoute,
  (user, list) => {
    if (!user || !list) return undefined;
    return user.externalId === list.creation.user?.externalId;
  },
);
